/**
 * The platform staff console.
 *
 * **No candidate data appears here, ever.** Everything on this page is about
 * organisations, the people who log in, and what has been done to them.
 *
 * There is deliberately no delete button for an organisation. Archiving stops
 * the hostname answering and deletes nothing; destroying a customer's candidate
 * data should not be one click from a table row.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Spinner from '../../components/Spinner';
import { Button, Chip, Empty, Field, Input, Notice, Select } from '../../components/ui';
import { ApiError, api, describeError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import ConsoleLayout from './ConsoleLayout';

interface Organization {
  id: string;
  slug: string;
  name: string;
  tier: string;
  state: string;
  tenantDb: string | null;
  tenantSchema: string | null;
  host: string | null;
  memberCount: number;
  requireMfa: boolean;
  createdAt: string;
}

interface Account {
  id: string;
  email: string;
  displayName: string | null;
  isPlatformAdmin: boolean;
  disabledAt: string | null;
  lastSeenAt: string | null;
  organizationCount: number;
}

interface AuditEntry {
  id: number;
  at: string;
  action: string;
  target: string | null;
  actorEmail: string | null;
  organizationSlug: string | null;
}

interface ProvisioningEntry {
  id: string;
  slug: string;
  name: string;
  state: string;
  steps: Array<{ step: string; state: string; attempts: number; error: string | null }>;
}

interface Overview {
  organizations: Organization[];
  accounts: Account[];
  audit: AuditEntry[];
  provisioning: ProvisioningEntry[];
}

export default function Console() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setOverview(await api.get<Overview>('/api/console'));
      setError(null);
    } catch (failure) {
      setError(describeError(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <ConsoleLayout>
        <Notice tone="danger">{error}</Notice>
      </ConsoleLayout>
    );
  }
  if (!overview) {
    return (
      <ConsoleLayout>
        <Spinner label="Loading the estate" />
      </ConsoleLayout>
    );
  }

  return (
    <ConsoleLayout>
      <div className="space-y-8">
        {overview.provisioning.length > 0 && (
          <Outstanding entries={overview.provisioning} onChange={load} />
        )}

        <CreateForOwner onChange={load} />

        <section className="panel p-6">
          <h2 className="mb-1 text-[15.5px] font-medium">Organisations</h2>
          <p className="mb-5 text-[13px] text-bone/45">
            {overview.organizations.length} in total.
          </p>

          {overview.organizations.length === 0 ? (
            <Empty title="No organisations yet." />
          ) : (
            <ul className="divide-y divide-veil/[0.06]">
              {overview.organizations.map((organization) => (
                <OrganizationRow
                  key={organization.id}
                  organization={organization}
                  onChange={load}
                />
              ))}
            </ul>
          )}
        </section>

        <section className="panel p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-[15.5px] font-medium">Accounts</h2>
              <p className="mt-1 text-[13px] text-bone/45">
                {overview.accounts.length} people can sign in.
              </p>
            </div>
            <Link to="/console/people" className="site-btn site-btn-ghost">
              Manage people
            </Link>
          </div>

          <ul className="divide-y divide-veil/[0.06]">
            {overview.accounts.slice(0, 12).map((account) => (
              <li key={account.id} className="flex flex-wrap items-center gap-3 py-3">
                <Link
                  to={`/console/people/${account.id}`}
                  className="min-w-0 flex-1 truncate font-code text-[12.5px] text-bone/70 hover:text-bone"
                >
                  {account.email}
                </Link>
                {account.isPlatformAdmin && <Chip tone="accent">Staff</Chip>}
                {account.disabledAt && <Chip tone="danger">Disabled</Chip>}
                <span className="text-[12px] text-bone/30">
                  {account.organizationCount === 0
                    ? 'no workspace'
                    : `${account.organizationCount} workspace${account.organizationCount === 1 ? '' : 's'}`}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="panel p-6">
          <h2 className="mb-5 text-[15.5px] font-medium">Recent activity</h2>
          {overview.audit.length === 0 ? (
            <Empty title="Nothing recorded yet." />
          ) : (
            <ul className="space-y-1.5 font-code text-[11.5px] leading-relaxed text-bone/50">
              {overview.audit.map((entry) => (
                <li key={entry.id} className="flex flex-wrap gap-x-3">
                  <span className="text-bone/25">{formatDate(entry.at)}</span>
                  <span className="text-bone/70">{entry.action}</span>
                  {entry.organizationSlug && <span>{entry.organizationSlug}</span>}
                  {entry.target && <span className="text-bone/40">{entry.target}</span>}
                  {entry.actorEmail && <span className="text-bone/25">by {entry.actorEmail}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </ConsoleLayout>
  );
}

function Outstanding({
  entries,
  onChange,
}: {
  entries: ProvisioningEntry[];
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const retry = async (slug: string) => {
    setBusy(slug);
    try {
      await api.post(`/api/console/organizations/${slug}/provision`);
      onChange();
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="panel border-warn/25 p-6">
      <h2 className="mb-1 text-[15.5px] font-medium text-warn">Not finished building</h2>
      <p className="mb-5 max-w-prose text-[13px] leading-relaxed text-bone/45">
        These workspaces do not answer yet. Every step is idempotent, so a retry picks up whatever
        did not land.
      </p>
      <ul className="divide-y divide-veil/[0.06]">
        {entries.map((entry) => (
          <li key={entry.id} className="flex flex-wrap items-start gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px]">
                {entry.name} <span className="font-code text-[11.5px] text-bone/30">{entry.slug}</span>
              </p>
              <ul className="mt-1 space-y-0.5">
                {entry.steps.map((step) => (
                  <li key={step.step} className="font-code text-[11px] text-bone/40">
                    {step.step}: {step.state}
                    {step.error && <span className="text-danger/70"> — {step.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <Button onClick={() => void retry(entry.slug)} busy={busy === entry.slug}>
              Retry
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CreateForOwner({ onChange }: { onChange: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ ownerExisted: boolean; invitationLink: string | null } | null>(
    null,
  );

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const created = await api.post<{ ownerExisted: boolean; invitationLink: string | null }>(
        '/api/console/organizations',
        { name, slug: slug || name, ownerEmail },
      );
      setResult(created);
      setName('');
      setSlug('');
      setOwnerEmail('');
      onChange();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : describeError(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel p-6">
      <h2 className="mb-1 text-[15.5px] font-medium">Create one for somebody</h2>
      <p className="mb-5 max-w-prose text-[13px] leading-relaxed text-bone/45">
        Name the owner by email. If they have no account yet the workspace is created ownerless
        with an owner invitation waiting — a state only staff can produce, and it resolves the
        moment they sign in.
      </p>

      {error && (
        <div className="mb-4">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}
      {result && (
        <div className="mb-4">
          <Notice tone="good">
            {result.ownerExisted
              ? 'Created, and the owner already had an account.'
              : 'Created ownerless. The owner joins when they sign in with that address.'}
            {result.invitationLink && (
              <>
                {' '}
                <code className="break-all font-code text-[11.5px]">{result.invitationLink}</code>
              </>
            )}
          </Notice>
        </div>
      )}

      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Address" hint="Blank derives it from the name.">
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="font-code"
            spellCheck={false}
          />
        </Field>
        <Field label="Owner email">
          <Input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            required
          />
        </Field>
        <div className="flex items-end pb-1">
          <Button type="submit" variant="primary" busy={busy}>
            Create
          </Button>
        </div>
      </form>
    </section>
  );
}

function OrganizationRow({
  organization,
  onChange,
}: {
  organization: Organization;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState('admin');

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChange();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  };

  const archived = organization.state === 'archived';

  return (
    <li className="py-3.5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px]">{organization.name}</p>
          <p className="truncate font-code text-[11.5px] text-bone/35">
            {organization.host ?? organization.slug} ·{' '}
            {organization.tenantDb ?? organization.tenantSchema ?? 'nowhere to live'}
          </p>
        </div>

        <Chip tone={archived ? 'muted' : organization.state === 'active' ? 'good' : 'warn'}>
          {organization.state}
        </Chip>
        <Chip tone="neutral">{organization.tier}</Chip>
        {organization.requireMfa && <Chip tone="accent">2FA required</Chip>}
        <span className="text-[12px] text-bone/30">
          {organization.memberCount} {organization.memberCount === 1 ? 'member' : 'members'}
        </span>

        <Select
          value={role}
          onChange={(event) => setRole(event.target.value)}
          className="w-auto py-1.5 text-[12.5px]"
          aria-label="Join as"
        >
          {['admin', 'recruiter', 'reviewer', 'interviewer'].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <Button
          variant="quiet"
          busy={busy}
          onClick={() =>
            act(() =>
              api.post(`/api/console/organizations/${organization.slug}/membership`, {
                action: 'join',
                role,
              }),
            )
          }
          title="Joining makes what you do attributable to a role rather than to a privilege."
        >
          Join
        </Button>
        <Button
          variant="quiet"
          busy={busy}
          onClick={() =>
            act(() =>
              api.post(`/api/console/organizations/${organization.slug}/membership`, {
                action: 'leave',
              }),
            )
          }
        >
          Leave
        </Button>
        <Button
          variant="quiet"
          busy={busy}
          onClick={() =>
            act(() =>
              api.post(
                `/api/console/organizations/${organization.slug}/${archived ? 'restore' : 'archive'}`,
              ),
            )
          }
        >
          {archived ? 'Restore' : 'Archive'}
        </Button>
      </div>

      {error && <p className="mt-2 text-[12.5px] text-danger">{error}</p>}
    </li>
  );
}
